/**
 * The disclosure-first announcement feed.
 *
 * Used on Home (three most recent) and across Investor Relations. When the
 * collection is empty it renders <PendingContent> — it never invents a row, and
 * there is no such thing as a "Test Announcement".
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText } from 'lucide-react';
import { Badge, PendingContent } from './ui-kit';
import { formatDate, formatDateTime } from '../lib/format';

export default function AnnouncementFeed({ items, loading, emptyMessage, onNavy = false }) {
  if (loading) {
    return (
      <div aria-busy="true" aria-live="polite">
        {[0, 1, 2].map((i) => (
          <div key={i} className="ann-row">
            <span className="c-badge" style={{ opacity: 0.4 }}>Loading</span>
            <span className="t-mono t-caption">—</span>
            <span className="t-secondary">Loading announcements…</span>
          </div>
        ))}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <PendingContent label="No announcements yet" onNavy={onNavy}>
        {emptyMessage}
      </PendingContent>
    );
  }

  return (
    <div data-testid="announcement-feed">
      {items.map((item, index) => (
        <article
          key={item.slug}
          className="ann-row"
          style={{ animationDelay: `${index * 60}ms` }}
          data-testid="announcement-row"
        >
          <div>
            <Badge category={item.category} />
          </div>
          <p className="t-mono t-secondary" style={{ fontSize: 13 }}>
            {formatDateTime(item.published_at)}
          </p>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 600 }}>
              <Link to={`/investor-relations/announcements/${item.slug}`}>{item.title}</Link>
            </h3>
            {/* SGX broadcasts one results filing per financial year under the same
                subtitle. The period it states is the only thing that tells them
                apart, so it is printed rather than left for the reader to open. */}
            {item.period_ended ? (
              <p className="t-micro t-caption" style={{ marginTop: 4 }} data-testid="announcement-period">
                Financial period ended {formatDate(item.period_ended)}
              </p>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {item.file_size ? (
              <span className="t-mono t-caption" style={{ fontSize: 12 }}>
                {item.file_size}
              </span>
            ) : null}
            {item.pdf_url ? (
              <a
                href={item.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="c-btn c-btn--tertiary"
                aria-label={`Open the PDF for ${item.title}`}
                data-testid="announcement-pdf-link"
              >
                <FileText size={15} aria-hidden="true" /> PDF
              </a>
            ) : item.sgx_detail_url ? (
              /* SGX publishes some filings as a structured record with no
                 attachment. Rather than a dead button, link the official record. */
              <a
                href={item.sgx_detail_url}
                target="_blank"
                rel="noopener noreferrer"
                className="c-btn c-btn--tertiary"
                aria-label={`Open the official SGXNet record for ${item.title}`}
                data-testid="announcement-sgx-link"
              >
                <ExternalLink size={15} aria-hidden="true" /> SGXNet
              </a>
            ) : null}
          </div>
        </article>
      ))}
    </div>
  );
}
